import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { z } from 'zod';

import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { normalizePhoneNumberToE164 } from '@apps/web/lib/phone_number_normalization';

// Common titles at a home care franchise location. "Other" reveals a free
// text input so we never block an invitee whose title is not listed.
const TITLE_OPTIONS = [
    'Owner',
    'President',
    'Vice President',
    'Franchise Owner',
    'Administrator',
    'General Manager',
    'Director of Operations',
    'Operations',
    'Office Manager',
    'Scheduling Manager',
    'Scheduler',
    'Scheduling Assistant',
    'Care Coordinator',
    'Recruiter',
    'HR Manager',
    'Other',
] as const;

export const Route = createFileRoute('/_auth/accept-invitation')({
    head: () => ({
        meta: [
            {
                title: 'Accept Invitation | Phoebe',
            },
        ],
    }),
    component: AcceptInvitationPage,
    validateSearch: z.object({
        token: z.string(),
    }),
});

function AcceptInvitationPage() {
    const { token } = Route.useSearch();
    const router = useRouter();
    const [loading, setLoading] = useState(true);
    const [invitationInfo, setInvitationInfo] = useState<{
        organization_name: string;
        inviter_name: string | null;
        email: string;
        first_name: string | null;
        last_name: string | null;
        expires_at: string;
        is_expired: boolean;
    } | null>(null);
    const [accepting, setAccepting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [phoneNumberInput, setPhoneNumberInput] = useState('');
    const [selectedTitle, setSelectedTitle] = useState('');
    const [otherTitle, setOtherTitle] = useState('');

    const normalizedPhoneNumber = normalizePhoneNumberToE164(phoneNumberInput);
    const resolvedTitle =
        selectedTitle === 'Other' ? otherTitle.trim() : selectedTitle;
    const detailsComplete =
        normalizedPhoneNumber !== null && resolvedTitle.length > 0;

    useEffect(() => {
        async function fetchInvitation() {
            try {
                const response = await getApiClient().get({
                    url: '/invitations/{token}',
                    pathParams: { token },
                });
                setInvitationInfo(response);
            } catch (err) {
                if (err instanceof Error) {
                    // Check for specific error messages and provide better UX
                    if (err.message.includes('already been accepted')) {
                        setError(
                            'This invitation has already been used. If you need access, please contact the person who invited you.',
                        );
                    } else if (err.message.includes('expired')) {
                        setError(
                            'This invitation has expired. Please contact the person who invited you for a new invitation.',
                        );
                    } else {
                        setError(err.message);
                    }
                } else {
                    setError('Invalid invitation link');
                }
            } finally {
                setLoading(false);
            }
        }

        fetchInvitation();
    }, [token]);

    const handleAccept = async () => {
        setAccepting(true);
        try {
            const response = await getApiClient().post({
                url: '/invitations/{token}/accept',
                pathParams: { token },
                body: {
                    phone_number: normalizedPhoneNumber,
                    title: resolvedTitle,
                },
            });

            if (response.is_new_user) {
                // Redirect to sign-up with invitation token and pre-filled data
                const params = new URLSearchParams({
                    invitation_token: token,
                    email: response.email || '',
                });
                if (response.first_name) {
                    params.append('first_name', response.first_name);
                }
                if (response.last_name) {
                    params.append('last_name', response.last_name);
                }

                window.location.href = `/sign-up?${params.toString()}`;
            } else {
                // Existing user - show success and redirect to sign in
                toast.success('You have been added to the organization!', {
                    persistNotification: false,
                });
                router.navigate({ to: '/sign-in' });
            }
        } catch (err) {
            toast.error(
                err instanceof Error
                    ? err.message
                    : 'Failed to accept invitation',
                { persistNotification: false },
            );
            setAccepting(false);
        }
    };

    if (loading) {
        return <Loader2 className="h-8 w-8 animate-spin" />;
    }

    if (error) {
        // Derive the title from the error message
        let errorTitle = 'Invalid Invitation';
        if (error.includes('already been used')) {
            errorTitle = 'Invitation Already Used';
        } else if (error.includes('expired')) {
            errorTitle = 'Invitation Expired';
        }

        return (
            <Card className="w-full max-w-md">
                <CardHeader>
                    <CardTitle>{errorTitle}</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-muted-foreground">{error}</p>
                </CardContent>
                <CardFooter>
                    <Button onClick={() => router.navigate({ to: '/sign-in' })}>
                        Go to Sign In
                    </Button>
                </CardFooter>
            </Card>
        );
    }

    if (!invitationInfo) {
        return null;
    }

    return (
        <Card className="w-full max-w-md">
            <CardHeader>
                <CardTitle>Join {invitationInfo.organization_name}</CardTitle>
                <CardDescription>
                    {invitationInfo.inviter_name
                        ? `${invitationInfo.inviter_name} has invited you to join their team on Phoebe`
                        : "You've been invited to join a team on Phoebe"}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {invitationInfo.is_expired ? (
                    <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
                        This invitation has expired. Please contact the person
                        who invited you for a new invitation.
                    </div>
                ) : (
                    <React.Fragment>
                        <div>
                            <p className="text-muted-foreground text-sm">
                                Email
                            </p>
                            <p className="font-medium">
                                {invitationInfo.email}
                            </p>
                        </div>
                        {invitationInfo.first_name && (
                            <div>
                                <p className="text-muted-foreground text-sm">
                                    Name
                                </p>
                                <p className="font-medium">
                                    {invitationInfo.first_name}{' '}
                                    {invitationInfo.last_name || ''}
                                </p>
                            </div>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="invitation-phone-number">
                                Mobile phone number
                            </Label>
                            <Input
                                id="invitation-phone-number"
                                type="tel"
                                autoComplete="tel"
                                placeholder="(555) 555-5555"
                                value={phoneNumberInput}
                                onChange={(event) =>
                                    setPhoneNumberInput(event.target.value)
                                }
                            />
                            <p className="text-muted-foreground text-xs">
                                This number is used for SMS notifications you
                                turn on in Settings.
                            </p>
                            {phoneNumberInput.trim().length > 0 &&
                                normalizedPhoneNumber === null && (
                                    <p className="text-destructive text-xs">
                                        Enter a valid phone number
                                    </p>
                                )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="invitation-title">
                                Your title at the company
                            </Label>
                            <Select
                                value={selectedTitle}
                                onValueChange={setSelectedTitle}
                            >
                                <SelectTrigger id="invitation-title">
                                    <SelectValue placeholder="Select your title" />
                                </SelectTrigger>
                                <SelectContent>
                                    {TITLE_OPTIONS.map((title) => (
                                        <SelectItem key={title} value={title}>
                                            {title}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {selectedTitle === 'Other' && (
                                <Input
                                    id="invitation-title-other"
                                    placeholder="Enter your title"
                                    value={otherTitle}
                                    onChange={(event) =>
                                        setOtherTitle(event.target.value)
                                    }
                                />
                            )}
                        </div>
                        <div className="text-muted-foreground text-sm">
                            This invitation expires on{' '}
                            {new Date(
                                invitationInfo.expires_at,
                            ).toLocaleDateString()}
                        </div>
                    </React.Fragment>
                )}
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
                <Button
                    variant="outline"
                    onClick={() => router.navigate({ to: '/sign-in' })}
                    disabled={accepting}
                >
                    Cancel
                </Button>
                {!invitationInfo.is_expired && (
                    <Button
                        variant="default"
                        onClick={handleAccept}
                        disabled={accepting || !detailsComplete}
                    >
                        {accepting ? (
                            <React.Fragment>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Processing…
                            </React.Fragment>
                        ) : (
                            'Continue'
                        )}
                    </Button>
                )}
            </CardFooter>
        </Card>
    );
}
